import { api } from "../../app/api";
import { command } from "../../app/command";
import { query } from "../../app/query";
import isEmpty from "../../common/helpers/isEmpty";
import { Options } from "../../common/types/requestOptions";
import { SalaryType } from "../orders/types/salaryType";
import { WorkPeriodType } from "../orders/types/workPeriodType";
import { WorkTimeType } from "../orders/types/workTimeType";
import { SalaryZone } from "../talentProfile/editForms/salaries/common";
import { accumulationCalculationKeys } from "../talentProfile/Employment/Accumulations/helpers";
import {
  ChangeFringeBenefits,
  FringeBenefitApiType,
  FringeBenefitType,
} from "../talentProfile/Employment/EmploymentAdditionalInfo/FringeBenefits/types";
import {
  EmploymentEndReason,
  ExpenseForEmployment,
} from "../talentProfile/Employment/EmploymentAdditionalInfo/types";
import { EventPayload } from "./../../common/types/Event";
import { Nullable } from "./../../common/types/nullable";
import { TargetedShiftSpec } from "./targetedShiftSalaries/targetedShiftSalariesApi";

export interface Salary {
  from: string;
  components: { [key: string]: string };
  massUpdateId?: string;
  tesSalaryTableSelection?: {
    tesSalaryTableId: string;
    rowId: string;
    columnId: string;
    mode: "FULL_SALARY" | "WITH_COMPS" | "INFO_ONLY";
  } | null;
}

export interface EmploymentAgreedHours {
  minHours: string;
  maxHours: string;
  weeksInPeriod: number;
}

export const CommonWorkTimeBankSettingMode = {
  NOT_SET: "NOT_SET",
  IMMEDIATE: "IMMEDIATE",
  WHEN_USED: "WHEN_USED",
};

export type CommonWorkTimeBankSettingModeType =
  (typeof CommonWorkTimeBankSettingMode)[keyof typeof CommonWorkTimeBankSettingMode];

export const WorkTimeBankSettingMode = {
  HYBRID: "HYBRID",
} as const;

export type WorkTimeBankSettingModeType =
  (typeof WorkTimeBankSettingMode)[keyof typeof WorkTimeBankSettingMode];

export type WorkTimeBankSetting = {
  from: string;
  mode: WorkTimeBankSettingModeType | CommonWorkTimeBankSettingModeType | null;
  min?: string;
  max?: string;
};

export type FormattedWorkTimeBankSetting = {
  from: Date;
  to: Date | null;
  mode: CommonWorkTimeBankSettingModeType | WorkTimeBankSettingModeType | null;
  min?: string;
  max?: string;
};

export type EditWorkTimeBankSettings = {
  employmentId: string;
  // Note: this is employment version
  version: number;
  settings: WorkTimeBankSetting[];
};

export type WorkTimeBankSettingsResponse = {
  settings: WorkTimeBankSetting[];
};

export type PeriodStartSetting = {
  from: string;
  periodStart: string;
};

export type AddEmploymentPeriodStartSetting = {
  employmentId: string;
  version: number;
  setting: PeriodStartSetting;
};

export type EditEmploymentPeriodStartSetting =
  AddEmploymentPeriodStartSetting & {
    from: string;
  };

export type RemoveEmploymentPeriodStartSetting = {
  employmentId: string;
  version: number;
  from: string;
};

// All three row events include the affected setting; edits also include its previous value.
export type EmploymentPeriodStartSettingEvent = {
  setting: PeriodStartSetting;
  previous?: PeriodStartSetting;
};

export interface EmploymentDetails {
  version: number;
  agreedStart: string;
  agreedEnd: string;
  workTitleId: string;
  employmentType: string;
  workTimeType: WorkTimeType;
  paymentPeriodClassId: string;
  annualHolidayAccumulationRule: string;
  annualLeavePaymentRule: string;
  tempEmploymentReason: string;
  employerName: string;
  isAveragingPeriodInUse: boolean;
  periodStartDate: string | null;
  periodStartBaseline?: string | null;
  periodStartSettings?: PeriodStartSetting[];
  periodType: WorkPeriodType;
  salaryType: SalaryType;
  salary: {
    ranges: Salary[];
  };
  wageSystem: SalaryType;
  probationEndDate?: string;
  agreedHours?: EmploymentAgreedHours;
  talentAgreesExtraWork?: boolean;
  otherAgreedInformation?: string;
  endReason?: EmploymentEndReason;
  timeBankSettings?: WorkTimeBankSetting[];
  flexibleHoursSettings?: WorkTimeBankSetting[];
  fringeBenefits?: FringeBenefitApiType[];
}

export interface Employment {
  employerId: string;
  talentId: string;
  continuousEmploymentId: string; // TODO get rid of this, not coming from backend
  contEmpId: string;
  employmentId: string;
  tesId: string;
  isShift: boolean;
  isCanceled: boolean;
  details: EmploymentDetails;
  targetedShiftSpec?: TargetedShiftSpec;
  workAgreementDocumentId: string | null;
}

export interface EmploymentAttributeFilters {
  talentId: string;
  date: string;
}

export interface EmploymentAttributes {
  version: number;
  rows: EmploymentAttribute[];
  supportedZones?: { [tesId: string]: string[] };
}

export interface EmploymentAttribute {
  id: string;
  workTitleId: string;
  tesId: string;
  totalSalary: string;
  version: number;
  zonedSalary?: { [zone: string]: string };
}

export interface EmploymentAttributesEditingFilters {
  talentId: string;
  workTitleId: string;
  tesId: string;
}

export interface AccumulationsForContinuousEmploymentFilters {
  scope: string;
  continuousEmploymentId: string;
  stateNames: string;
  from: string;
  to: string;
}

export interface AccumulationsForEmploymentFilters {
  scope: string;
  employmentId: string;
  stateNames: string;
  from: string;
  to: string;
}

export interface TalentContinuousEmploymentAccumulation {
  continuousEmploymentId: string;
  dateRange: { start: string; end: string };
  eezyCompanyId: string;
  accumulationsByStateName: Record<string, string>;
  accumulationsByEmploymentId: Record<string, Record<string, string>>;
}

export interface TalentAccumulationsResponse {
  talentId: string;
  accumulations: TalentContinuousEmploymentAccumulation[];
}

export type AccumulationCalculationKey =
  (typeof accumulationCalculationKeys)[number];

export interface OngoingKta {
  valueDate: string;
  salary: string;
  workTime: string;
}

export interface KtaAvg {
  [date: string]: OngoingKta;
}

export interface AccumulationCalculationMultiple {
  ongoingAverages?: KtaAvg;
}

export interface AccumulationCalculationSingle {
  partitionKey?: string;
  workTime?: string;
  completedLimits?: string;
}

export interface AccumulationCalculationGroupMath {
  ongoingAverages?: KtaAvg;
  partitions?: Record<
    string,
    { inputs: Record<string, string>; inputOperand: string }
  >;
}

export type AccumulationCalculation =
  | AccumulationCalculationMultiple
  | AccumulationCalculationSingle
  | AccumulationCalculationGroupMath;

export interface AccumDateRow extends Record<string, any> {
  custOrgName?: string;
  year?: string;
  month?: string;
  custOrgId?: string;
  assignmentId?: string;
  time?: string;
  days?: string;
  deltaId?: string;
  lomakeskiansiolaskenta?:
    | AccumulationCalculationMultiple
    | AccumulationCalculationGroupMath
    | string;
  keskiansiolaskenta?: AccumulationCalculationMultiple | string;
  työajanlyhennyslaskenta?: AccumulationCalculationSingle | string;
  vuosivapaalaskenta?: AccumulationCalculationSingle | string;
  viikkokeskityömäärälaskenta?: AccumulationCalculationSingle | string;
}

export type AccumDateData = {
  date: Nullable<string>;
  rows: AccumDateRow[];
};

export interface AccumulationsForEmploymentResponse {
  meta: {
    keys: string[];
    stateNames: string[];
  };
  data: AccumDateData[];
}

export interface AddAccumDelta {
  isInternal: boolean;
  contEmpId: string;
  employmentId: Nullable<string>;
  year?: string;
  month?: string;
  custOrgId?: string;
  // bulkUpdateId not supported by backend, maybe never will be? But piece of this taken to requestId
  bulkUpdateId?: string;
  values: any; // TODO in bulk salary that time days thing just did not want to work, hope this does not break anything
  // values: {
  //   time: string; // TODO: fix, these are dynamic stateNames
  //   days: string;
  // };
}

export interface AccumDeltaAdded {
  contEmpId: string;
  employmentId?: string;
  customerOrgId?: string;
  year?: string;
  month?: string;
  date: string;
  stateName: string;
  isInternal: boolean;
  assignmentId?: string;
  value: string;
}

export interface DelAccumDelta {
  id: string;
}

export type SalaryPerDate = {
  date: string;
  salary: { [k: string]: string };
  zonedSalary?: { [zone: string]: { [k: string]: string } };
  // this is effectively TesSalaryTableSelectionProvenance from tesSalaryTablesApi.ts
  tesSalaryTableSelection?: {
    tesSalaryTableId: string;
    rowId: string;
    columnId: string;
    mode: "FULL_SALARY" | "WITH_COMPS" | "INFO_ONLY";
  } | null;
  tesSalaryTableName?: string | null;
  tesSalaryTableEffectiveDate?: string | null;
  tesSalaryTableSelectionLabel?: string | null;
  previewSalary?: { [k: string]: string } | null;
  targetTesSalaryTableName?: string | null;
  targetTesSalaryTableEffectiveDate?: string | null;
  targetTesSalaryTableSelectionLabel?: string | null;
};

export interface EmploymentAttributesForEditing {
  version: number;
  workTitleId: string;
  tesId: string;
  validFrom: string | null;
  salaryComponents: string[];
  salaryPerDate: SalaryPerDate[];
  ended?: string;
  salaryZones?: (SalaryZone | string)[];
}

export type SalaryValues = { [k: string]: string };
export type ZonedSalaryValues = { [zone: string]: SalaryValues };

export interface SalaryParams {
  salary: SalaryValues;
  zonedSalary?: ZonedSalaryValues;
}

export interface SetEmpAttrSalaryParams extends SalaryParams {
  talentId: string;
  version: number;
  workTitleId: string;
  tesId: string;
  from: string;
  massUpdateId?: string;
}

export interface ClearEmpAttrSalaryParams {
  talentId: string;
  version: number;
  workTitleId: string;
  tesId: string;
  from: string;
}

export interface ClearEmpAttrSalaryResponse {
  workTitleId: string;
  tesId: string;
  from: string;
}

export type EndEmpAttrSalaryParams = ClearEmpAttrSalaryParams;
export type EndEmpAttrSalaryResponse = ClearEmpAttrSalaryResponse;

export interface SetEmpAttrSalaryResponse {
  id: string;
  version: number;
  name: string;
  payload: {
    workTitleId: string;
    tesId: string;
    from: string;
    salary: {
      [k: string]: string;
    };
  };
}

export enum Kind {
  Draft = "DRAFT",
  Official = "OFFICIAL",
}

export interface ExpensesForEmploymentFilters {
  employmentId: string;
  from: string;
  to: string;
  kind: Kind;
}

export interface EditEmpSalary {
  employmentId: string;
  version: number;
  setSalary?: Salary;
  remove?: string;
}

export interface EmpSalaryChanged {
  removes: string[];
  addsAndMods: Salary[];
}

export interface EditEmploymentPeriodStart {
  employmentId: string;
  version: number;
  periodStart: string;
}

export interface EmploymentPeriodStartChanged {
  periodStart: string;
}

export interface EditEmploymentOtherAgreedInformation {
  employmentId: string;
  version: number;
  otherAgreedInformation: string;
}

export interface EmploymentOtherAgreedInformationChanged {
  otherAgreedInformation: string;
}

export interface EditEmploymentAnnualLeavePaymentRule {
  employmentId: string;
  version: number;
  annualLeavePaymentRule: string;
}

export interface EmploymentAnnualLeavePaymentRuleChanged {
  annualLeavePaymentRule: string;
}

export interface EditEmploymentAnnualHolidayAccumulationRule {
  employmentId: string;
  version: number;
  annualHolidayAccumulationRule: string;
}

export interface EmploymentAnnualHolidayAccumulationRuleChanged {
  annualHolidayAccumulationRule: string;
}

export interface EmploymentEndChanged {
  endDate: string;
  reason: EmploymentEndReason;
}

export interface SetEmploymentEnd {
  employmentId: string;
  version: number;
  endDate: string;
  reason: EmploymentEndReason;
}

export interface AgreedHours {
  minHours: string;
  maxHours: string;
  weeksInPeriod: number;
}

export interface EditEmploymentAgreedHours {
  employmentId: string;
  version: number;
  agreedHours: AgreedHours;
}

export interface TargetingRule {
  custCompanyId: string;
  custOrgs: string[];
  workLocs: string[];
  workTitles: string[];
}

export interface EmploymentTargetedShiftSpec {
  version: number;
  targetingRules: TargetingRule[];
  salaries: {};
}

export interface SetShiftTargetingRules {
  employmentId: string;
  version: number;
  rules: TargetingRule[];
}

export interface SetTargetedShiftsValidationAssignment {
  employmentId: string;
  validationAssignmentId: string;
  version: number;
}

// TODO: remove when mock data when real data is available
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const getMockedAccumulations = (
  response: AccumulationsForEmploymentResponse,
) => {
  const stateNames = response.meta.stateNames;

  let mockedData: AccumDateData[] = [];

  if (stateNames.includes("lomakeskiansiolaskenta")) {
    mockedData = [
      {
        date: "2024-01-14",
        rows: [
          {
            lomakeskiansiolaskenta: JSON.stringify({
              ongoingAverages: {
                "2022-04-01": {
                  valueDate: "2022-05-01",
                  salary: "186.8450000000000000",
                  workTime: "9.50000000",
                },
                "2022-05-01": {
                  valueDate: "2022-06-01",
                  salary: "90.8450000000000000",
                  workTime: "6.50000000",
                },
              },
            }),
          },
        ],
      },
    ];
  } else if (stateNames.includes("keskiansiolaskenta")) {
    mockedData = [
      {
        date: "2024-01-14",
        rows: [
          {
            keskiansiolaskenta: JSON.stringify({
              ongoingAverages: {
                "2022-04-01": {
                  valueDate: "2022-05-01",
                  salary: "142.8450000000000000",
                  workTime: "10.50000000",
                },
                "2022-05-01": {
                  valueDate: "2022-06-01",
                  salary: "122.8450000000000000",
                  workTime: "8.50000000",
                },
              },
            }),
          },
        ],
      },
    ];
  } else if (stateNames.includes("työajanlyhennyslaskenta")) {
    mockedData = [
      {
        date: "2024-01-14",
        rows: [
          {
            työajanlyhennyslaskenta: JSON.stringify({
              partitionKey: "2022-12-31 2022-01-01 2022-12-31",
              workTime: "144",
              completedLimits: "2",
            }),
          },
        ],
      },
    ];
  } else if (stateNames.includes("vuosivapaalaskenta")) {
    mockedData = [
      {
        date: "2024-01-14",
        rows: [
          {
            vuosivapaalaskenta: JSON.stringify({
              partitionKey: "2022-12-31 2022-01-01 2022-12-31",
              workTime: "144",
              completedLimits: "2",
            }),
          },
        ],
      },
    ];
  } else {
    mockedData = [];
  }

  return {
    meta: response.meta,
    data: mockedData,
  };
};

export const getEmploymentById = (
  employmentId: string,
  { requestId }: Options,
) => {
  return api.get<Employment>("/projections/qry/employmentById", {
    requestId,
    queryParams: {
      id: employmentId,
    },
    auth: true,
  });
};

export const employmentAttributesOverview = (
  filters: EmploymentAttributeFilters,
  { requestId }: Options,
): Promise<EmploymentAttributes> => {
  return api
    .get<EmploymentAttributes>(
      "/projections/qry/employmentAttributesOverview",
      {
        requestId,
        queryParams: {
          talentId: filters.talentId,
          date: filters.date,
        },
        auth: true,
      },
    )
    .then((res) => {
      // modify the response to previous data model for frontend to prevent large refactoring
      if (!isEmpty(res.data)) {
        return {
          version: res.data.version,
          rows: res.data.rows.map((row) => ({
            ...row,
            version: res.data.version,
          })),
          supportedZones: res.data.supportedZones,
        };
      }
      return {
        version: 0,
        rows: [],
      };
    });
};

export const employmentAttributesForEditing = (
  filters: EmploymentAttributesEditingFilters,
  { requestId }: Options,
) => {
  return api
    .get<Promise<EmploymentAttributesForEditing>>(
      "/projections/qry/employmentAttributesForEditing",
      {
        requestId,
        queryParams: {
          ...filters,
        },
        auth: true,
      },
    )
    .then((res) => res.data);
};

export const setEmpAttrSalary = (
  params: SetEmpAttrSalaryParams,
  { requestId }: Options,
) => {
  return api
    .post<SetEmpAttrSalaryResponse[]>("/working/cmd/setEmpAttrSalary", {
      requestId,
      auth: true,
      body: {
        ...params,
      },
    })
    .then((res) => res.data[0]);
};

export const clearEmploymentAttributesSalaryDate = (
  params: ClearEmpAttrSalaryParams,
  { requestId }: Options,
) => {
  return api
    .post<EventPayload<ClearEmpAttrSalaryResponse>[]>(
      "/working/cmd/clrEmpAttrSalary",
      {
        requestId,
        auth: true,
        body: {
          ...params,
        },
      },
    )
    .then((res) => res.data[0]);
};

export const endEmploymentAttributesSalaryDate = (
  params: EndEmpAttrSalaryParams,
  { requestId }: Options,
) => {
  return api
    .post<EventPayload<EndEmpAttrSalaryResponse>[]>(
      "/working/cmd/endEmpAttrSalary",
      {
        requestId,
        auth: true,
        body: {
          ...params,
        },
      },
    )
    .then((res) => res.data[0]);
};

const parseAccumulationData = (data: AccumDateData[]): AccumDateData[] => {
  return data.map((entry) => ({
    ...entry,
    rows: entry.rows.map((row) => {
      const parsedRow: AccumDateRow = { ...row };
      // TODO: can isAccumulationCalculationKey be used here?
      accumulationCalculationKeys.forEach((key) => {
        if (key in parsedRow && typeof parsedRow[key] === "string") {
          const typed = parsedRow[key] as string;
          // in case if TES engine returns null as a key in JSON, which is not valid JSON or
          // if some old data is stored like this -> replace null with "NO_DATE"
          // also the key wanted to be actual string with some
          // meaning which can't be mixed up to date (some cases the key is date
          const correctedResponse = typed.replaceAll("{null:", '{"NO_DATE":');
          parsedRow[key] = JSON.parse(correctedResponse as string);
        }
      });
      return parsedRow;
    }),
  }));
};

export const accumulationsForContinuousEmployment = (
  filters: AccumulationsForContinuousEmploymentFilters,
  { requestId }: Options,
): Promise<AccumulationsForEmploymentResponse> =>
  api
    .get<AccumulationsForEmploymentResponse>(
      "/working/qry/accumulationsForContinuousEmployment",
      {
        requestId,
        queryParams: {
          ...filters,
        },
        auth: true,
      },
    )
    .then((res) => {
      return {
        ...res.data,
        data: parseAccumulationData(res.data.data),
      };
    });

export const accumulationsForEmployment = (
  filters: AccumulationsForEmploymentFilters,
  { requestId }: Options,
): Promise<AccumulationsForEmploymentResponse> =>
  api
    .get<AccumulationsForEmploymentResponse>(
      "/working/qry/accumulationsForEmployment",
      {
        requestId,
        queryParams: {
          ...filters,
        },
        auth: true,
      },
    )
    .then((res) => {
      return {
        ...res.data,
        data: parseAccumulationData(res.data.data),
      };
      // return {
      //   ...res.data,
      //   data: parseAccumulationData(getMockedAccumulations(res.data).data),
      // };
    });

export const getAccumulationsForTalent = (
  talentId: string,
  options: Options = {},
): Promise<TalentAccumulationsResponse> =>
  query<{ talentId: string }, TalentAccumulationsResponse>(
    "/working/qry/accumulationsForTalent",
    { talentId },
    options,
  );

export const addAccumDelta = (params: AddAccumDelta, { requestId }: Options) =>
  api
    .post<EventPayload<AccumDeltaAdded>[]>("/working/cmd/addAccumDelta", {
      requestId,
      auth: true,
      body: {
        ...params,
      },
    })
    .then((res) => res.data);

export const delAccumDelta = (params: DelAccumDelta, { requestId }: Options) =>
  api
    .post<EventPayload<any>>("/working/cmd/delAccumDelta", {
      requestId,
      auth: true,
      body: {
        ...params,
      },
    })
    .then((res) => res.data);

export const expensesForEmployment = (
  filters: ExpensesForEmploymentFilters,
  { requestId }: Options,
) => {
  return api
    .get<Promise<ExpenseForEmployment[]>>(
      "/working/qry/expensesForEmployment",
      {
        requestId,
        queryParams: {
          ...filters,
        },
        auth: true,
      },
    )
    .then((res) => res.data);
};

export const editEmpSalary = async (
  body: EditEmpSalary,
  { requestId }: Options,
): Promise<EventPayload<EmpSalaryChanged>[]> =>
  await api
    .post<EventPayload<EmpSalaryChanged>[]>("/working/cmd/editEmpSalary", {
      requestId,
      auth: true,
      body,
    })
    .then((res) => res.data);

export const changePeriodStart = async (
  body: EditEmploymentPeriodStart,
  { requestId }: Options,
): Promise<EventPayload<EmploymentPeriodStartChanged>[]> =>
  await api
    .post<EventPayload<EmploymentPeriodStartChanged>[]>(
      "/working/cmd/chgEmpPeriodStart",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const changeEmpOtherAgreedInformation = async (
  body: EditEmploymentOtherAgreedInformation,
  { requestId }: Options,
): Promise<EventPayload<EmploymentOtherAgreedInformationChanged>[]> =>
  await api
    .post<EventPayload<EmploymentOtherAgreedInformationChanged>[]>(
      "/working/cmd/chgEmpOthArgInfo",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const changeEmpAnnualLeavePaymentRule = async (
  body: EditEmploymentAnnualLeavePaymentRule,
  { requestId }: Options,
): Promise<EventPayload<EmploymentAnnualLeavePaymentRuleChanged>[]> =>
  await api
    .post<EventPayload<EmploymentAnnualLeavePaymentRuleChanged>[]>(
      "/working/cmd/chgEmpAnnualLeavePaymentRule",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const changeEmpAnnualHolidayAccumulationRule = async (
  body: EditEmploymentAnnualHolidayAccumulationRule,
  { requestId }: Options,
): Promise<EventPayload<EmploymentAnnualHolidayAccumulationRuleChanged>[]> =>
  await api
    .post<EventPayload<EmploymentAnnualHolidayAccumulationRuleChanged>[]>(
      "/working/cmd/chgEmpAnnualHolidayAccumulationRule",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const setEmploymentEnd = async (
  body: SetEmploymentEnd,
  { requestId }: Options,
): Promise<EventPayload<EmploymentEndChanged>[]> =>
  await api
    .post<EventPayload<EmploymentEndChanged>[]>(
      "/working/cmd/setEmploymentEnd",
      {
        requestId,
        auth: true,
        body,
      },
    )
    .then((res) => res.data);

export const changeEmploymentAgreedHours = async (
  body: EditEmploymentAgreedHours,
  options: Options = {},
): Promise<EventPayload<EmploymentAgreedHours>[]> =>
  command<EditEmploymentAgreedHours, EmploymentAgreedHours>(
    "/working/cmd/changeEmploymentAgreedHours",
    body,
    options,
  );

export const addEmploymentPeriodStartSetting = (
  body: AddEmploymentPeriodStartSetting,
  options: Options = {},
): Promise<EventPayload<EmploymentPeriodStartSettingEvent>[]> =>
  command<AddEmploymentPeriodStartSetting, EmploymentPeriodStartSettingEvent>(
    "/working/cmd/addEmploymentPeriodStartSetting",
    body,
    options,
  );

export const editEmploymentPeriodStartSetting = (
  body: EditEmploymentPeriodStartSetting,
  options: Options = {},
): Promise<EventPayload<EmploymentPeriodStartSettingEvent>[]> =>
  command<EditEmploymentPeriodStartSetting, EmploymentPeriodStartSettingEvent>(
    "/working/cmd/editEmploymentPeriodStartSetting",
    body,
    options,
  );

export const removeEmploymentPeriodStartSetting = (
  body: RemoveEmploymentPeriodStartSetting,
  options: Options = {},
): Promise<EventPayload<EmploymentPeriodStartSettingEvent>[]> =>
  command<
    RemoveEmploymentPeriodStartSetting,
    EmploymentPeriodStartSettingEvent
  >("/working/cmd/removeEmploymentPeriodStartSetting", body, options);

export const changeWorkTimeBankSettings = async (
  body: EditWorkTimeBankSettings,
  options: Options = {},
): Promise<EventPayload<WorkTimeBankSettingsResponse>[]> =>
  command<EditWorkTimeBankSettings, EditWorkTimeBankSettings>(
    "/working/cmd/chgEmpWorktimeBankSettings",
    body,
    options,
  );

export const changeFlexibleHoursSettings = async (
  body: EditWorkTimeBankSettings,
  options: Options = {},
): Promise<EventPayload<WorkTimeBankSettingsResponse>[]> =>
  command<EditWorkTimeBankSettings, EditWorkTimeBankSettings>(
    "/working/cmd/chgEmpFlexibleHoursSettings",
    body,
    options,
  );

export const getEmploymentTargetedShiftSpec = async (
  employmentId: string,
  options: Options = {},
): Promise<EmploymentTargetedShiftSpec> =>
  query<{ employmentId: string }, EmploymentTargetedShiftSpec>(
    "/projections/qry/empTargetedShiftSpec",
    { employmentId },
    options,
  );

export const doSetShiftTargetingRules = async (
  body: SetShiftTargetingRules,
  options: Options = {},
): Promise<EventPayload<SetShiftTargetingRules>[]> =>
  command<SetShiftTargetingRules, SetShiftTargetingRules>(
    "/working/cmd/setShiftTargetingRules",
    body,
    options,
  );

export const updateTargetedShiftsValidationAssignment = async (
  body: SetTargetedShiftsValidationAssignment,
  options: Options = {},
): Promise<EventPayload<SetTargetedShiftsValidationAssignment>[]> =>
  command<
    SetTargetedShiftsValidationAssignment,
    SetTargetedShiftsValidationAssignment
  >("/working/cmd/setTargetedShiftsValidationAssignment", body, options);

export const getFringeBenefitTypes = (
  filters: {},
  options: Options = {},
): Promise<FringeBenefitType[]> =>
  query<{}, FringeBenefitType[]>(
    "/working/qry/fringeBenefitExpenseTypes",
    filters,
    options,
  );

export const replaceFringeBenefits = async (
  body: ChangeFringeBenefits,
  options: Options = {},
): Promise<EventPayload<{ fringeBenefits: FringeBenefitApiType[] }>[]> =>
  command<ChangeFringeBenefits, { fringeBenefits: FringeBenefitApiType[] }>(
    "/working/cmd/chgEmpFringeBenefits",
    body,
    options,
  );
